/**
 * Custom Add to Cart Extension for product-form.js
 * Extends the existing ProductForm functionality without redefining the custom element
 * SINGLE INSTANCE VERSION - Only runs once per page
 */

// Wrap everything in an IIFE (Immediately Invoked Function Expression) to avoid global scope pollution
(function () {
  'use strict';

  //initialize
  window.DAC = window.DAC || {};

  // Global flag to prevent multiple initializations
  if (window.DAC.customAddToCartInitialized) {
    return; // Already initialized, exit early
  }

  // Mark as initialized
  window.DAC.customAddToCartInitialized = true;

  // Use event delegation for better performance and automatic handling of new buttons
  function initializeWithEventDelegation() {
    // Remove any existing listener first
    document.removeEventListener('click', delegatedClickHandler);

    // Add single delegated event listener to document
    document.addEventListener('click', delegatedClickHandler);
  }

  function delegatedClickHandler(evt) {
    // Check if clicked element is a custom add-to-cart button
    const button = evt.target.closest('.custom-add-to-cart-btn');
    if (button) {
      console.log(`atc button clicked`);
      // Create a modified event object with correct currentTarget
      const modifiedEvent = {
        ...evt,
        currentTarget: button,
        preventDefault: evt.preventDefault.bind(evt),
        stopPropagation: evt.stopPropagation.bind(evt),
      };
      handleCustomButtonClick.call(button, modifiedEvent);
    }
  }

  async function handleCustomButtonClick(evt) {
    const button = evt.currentTarget || this;

    // Ensure we have a valid DOM element
    if (!button || typeof button.getAttribute !== 'function') {
      console.error('Invalid button element:', button);
      return;
    }

    // Use getAttribute for better browser compatibility (IE9+)
    const buttonBehavior = button.getAttribute('data-button-behavior') || 'open_cart_drawer';
    const discountCode = button.getAttribute('data-discount-code') || null;

    // Check if button is disabled
    if (button.getAttribute('aria-disabled') === 'true' || button.disabled) {
      return;
    }

    // If there's a discount code, we need to handle both behaviors with custom logic
    if (discountCode) {
      evt.preventDefault(); // Prevent default form submission
      await handleCustomSubmission(button, buttonBehavior, discountCode);
    } else {
      console.log(`no discount code found`);
      // No discount code - handle based on behavior
      if (buttonBehavior === 'direct_to_checkout') {
        evt.preventDefault();
        await handleDirectCheckout(button, null);
      }
      // For cart drawer without discount, let default product-form handler run
    }
  }

  async function handleCustomSubmission(button, buttonBehavior, discountCode) {
    // Find the parent product-form element
    const productForm = button.closest('product-form');
    if (!productForm) {
      return;
    }

    // Get the form element
    const form = productForm.querySelector('form');
    if (!form) {
      return;
    }

    // Set loading state
    setLoadingState(button, true);

    try {
      // Step 1: Add items to cart
      const cartResponse = await addToCart(form, button);

      if (cartResponse.status) {
        // Handle error (out of stock, etc.)
        handleError(button, cartResponse.description);
        return;
      }

      // Step 2: Apply discount codes BEFORE opening cart drawer
      if (discountCode) {
        //  console.log('Applying discount codes before opening cart...');
        await applyDiscountToCart(discountCode);
      }

      // Step 3: Handle post-cart behavior with refreshed cart data
      if (buttonBehavior === 'direct_to_checkout') {
        // Redirect to checkout (discount already applied to cart)
        window.location.href = '/checkout';
      } else {
        // Get fresh cart data after discount application, then open cart drawer
        await handleCartDrawerUpdateWithDiscount(button);
      }
    } catch (error) {
      console.error('Error during cart submission:', error);
    } finally {
      // Only remove loading state if we're not redirecting
      if (buttonBehavior !== 'direct_to_checkout') {
        setLoadingState(button, false);
      }
    }
  }

  // Handle cart drawer update after discount application
  async function handleCartDrawerUpdateWithDiscount(button) {
    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

    if (!cart) {
      console.warn('No cart element found');
      return;
    }

    try {
      // Get completely fresh cart data with discounts applied
      //  console.log('Fetching fresh cart data with discounts...');

      // Fetch updated cart sections (same method the theme uses)
      const sectionsToRender = cart.getSectionsToRender ? cart.getSectionsToRender() : [];

      if (sectionsToRender.length > 0) {
        // Fetch fresh sections
        const sectionsUrl =
          window.location.pathname + '?sections=' + sectionsToRender.map((section) => section.id).join(',');

        const sectionsResponse = await fetch(sectionsUrl, {
          method: 'GET',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
          },
        });

        if (sectionsResponse.ok) {
          const sections = await sectionsResponse.json();
          // console.log('Fresh sections loaded:', sections);

          // Update cart with fresh sections that include discount info
          if (cart.renderContents) {
            cart.renderContents({ sections });
          }
        } else {
          console.warn('Failed to fetch fresh sections, using fallback method');
          await fallbackCartUpdate(cart);
        }
      } else {
        //  console.log('No sections to render, using fallback method');
        await fallbackCartUpdate(cart);
      }

      // Remove empty cart class if present
      if (cart.classList.contains('is-empty')) {
        cart.classList.remove('is-empty');
      }

      // Open the cart drawer with updated content
      if (cart.classList.contains('drawer') && cart.open) {
        // console.log('Opening cart drawer with discount applied');
        cart.open();
      }
    } catch (error) {
      console.error('Error updating cart drawer with discount:', error);
      // Fallback to basic cart update
      await fallbackCartUpdate(cart);
    }

    // Handle quick-add modal case
    const quickAddModal = button.closest('quick-add-modal');
    if (quickAddModal) {
      document.body.addEventListener(
        'modalClosed',
        () => {
          setTimeout(async () => {
            // Refresh cart after modal closes
            await fallbackCartUpdate(cart);
          });
        },
        { once: true }
      );
      quickAddModal.hide(true);
    }
  }

  // Fallback method to update cart
  async function fallbackCartUpdate(cart) {
    try {
      console.log('Using fallback cart update method');

      // Get basic cart data
      const cartResponse = await fetch('/cart.js', {
        method: 'GET',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (cartResponse.ok) {
        const cartData = await cartResponse.json();
        //  console.log('Fallback cart data:', cartData);

        if (cart.renderContents) {
          cart.renderContents(cartData);
        }

        // Open cart drawer if it's not already open
        if (cart.classList.contains('drawer') && cart.open) {
          cart.open();
        }
      }
    } catch (error) {
      console.error('Fallback cart update failed:', error);
    }
  }

  async function handleDirectCheckout(button, discountCodes) {
    // Find the parent product-form element
    const productForm = button.closest('product-form');
    if (!productForm) {
      return;
    }

    const form = productForm.querySelector('form');
    if (!form) {
      return;
    }

    // Set loading state
    setLoadingState(button, true);

    try {
      // Add items to cart
      const cartResponse = await addToCart(form, button);

      if (cartResponse.status) {
        // handleError(button, cartResponse.description);
        return;
      }

      // Handle multiple discount codes for direct checkout
      if (discountCodes) {
        const codes = discountCodes
          .split(',')
          .map((code) => code.trim())
          .filter((code) => code !== '');

        if (codes.length > 0) {
          // For direct checkout with multiple codes, we'll use the first code in the URL
          // and apply the rest via the cart before redirecting
          const primaryCode = codes[0];
          const additionalCodes = codes.slice(1);

          // Apply additional codes to cart first
          if (additionalCodes.length > 0) {
            // console.log('Applying additional discount codes before checkout:', additionalCodes);
            await applyDiscountToCart(additionalCodes.join(','));
          }

          // Redirect with primary discount code
          const shopDomain = window.location.hostname;
          window.location.href = `https://${shopDomain}/discount/${primaryCode}/?redirect=/checkout`;
          return;
        }
      }

      // No discount codes, direct to checkout
      window.location.href = '/checkout';
    } catch (error) {
      //handleError(button, 'Unable to add item to cart. Please try again.');
    }
    // Don't remove loading state since we're redirecting
  }

  async function addToCart(form, button) {
    console.log(`adding to cart`);
    // Get cart element for sections update
    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

    // Prepare form data
    const formData = new FormData(form);

    // Add cart sections for drawer refresh if cart exists (matching product-form.js)
    if (cart) {
      formData.append(
        'sections',
        cart.getSectionsToRender().map((section) => section.id)
      );
      formData.append('sections_url', window.location.pathname);
      cart.setActiveElement(document.activeElement);
    }

    // Check for selling plans in form data and validate
    console.log(`checking selling plans`);
    const sellingPlanInputs = form.querySelectorAll('input[name*="selling_plan"]');
    if (sellingPlanInputs.length > 0) {
      sellingPlanInputs.forEach((input) => {
        // Validate selling plan ID format (should be numeric)
        if (input.value && !/^\d+$/.test(input.value.trim())) {
          // Remove invalid selling plan to prevent error
          input.remove();
        } else if (input.value && input.value.trim() === '') {
          input.remove();
        }
      });
    } else {
      console.log(`no selling plans`);
    }

    // Use the exact same configuration as product-form.js
    const config = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest',
      },
    };

    // Remove Content-Type header for FormData (let browser set it with boundary)
    delete config.headers['Content-Type'];
    config.body = formData;

    // Use the same cart add URL as product-form.js
    const cartAddUrl = routes.cart_add_url;

    try {
      const response = await fetch(cartAddUrl, config);

      // Get response text first to see what we're actually getting
      const responseText = await response.text();

      // Try to parse as JSON
      try {
        const jsonResponse = JSON.parse(responseText);

        // If there's a selling plan error, provide more helpful info
        if (
          jsonResponse.status === 422 &&
          jsonResponse.description &&
          jsonResponse.description.includes('selling plan')
        ) {
          console.error('custom ATC Selling plan error details:', {
            message: jsonResponse.message,
            description: jsonResponse.description,
            formData: Array.from(formData.entries()),
          });
        }

        return jsonResponse;
      } catch (jsonError) {
        console.error('JSON parse error:', jsonError);
        throw new Error(`Invalid JSON response: ${responseText.substring(0, 200)}`);
      }
    } catch (error) {
      console.error('Error in addToCart:', error);
      throw error;
    }
  }

  async function applyDiscountToCart(discountCodes) {
    // Parse comma-separated discount codes
    const codes = discountCodes
      .split(',')
      .map((code) => code.trim())
      .filter((code) => code !== '');

    if (codes.length === 0) {
      return;
    }

    // Apply each discount code
    for (const discountCode of codes) {
      try {
        // Apply via discount URL (the method Shopify recognizes)
        const discountUrl = `/discount/${discountCode}`;
        const discountResponse = await fetch(discountUrl, {
          method: 'GET',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
          },
          credentials: 'same-origin', // Ensure cookies are included
        });

        if (discountResponse.ok) {
          console.log(`Successfully applied discount code: ${discountCode}`);
        } else {
          console.warn(`Failed to apply discount code: ${discountCode}, status: ${discountResponse.status}`);
        }

        // Small delay between discount applications
        if (codes.length > 1) {
          await new Promise((resolve) => setTimeout(resolve, 200));
        }
      } catch (error) {
        console.warn(`Error applying discount code ${discountCode}:`, error);
      }
    }

    // Add a small delay to ensure discounts are fully processed
    await new Promise((resolve) => setTimeout(resolve, 300));
  }

  function handleError(button, errorMessage) {
    // Check if this is a sold out error
    const soldOutMessage = button.querySelector('.sold-out-message');

    if (errorMessage && errorMessage.toLowerCase().includes('sold out')) {
      if (soldOutMessage) {
        button.setAttribute('aria-disabled', 'true');
        button.querySelector('span').classList.add('hidden');
        soldOutMessage.classList.remove('hidden');
        return;
      }
    }

    // You could integrate with your theme's notification system here
    if (errorMessage) {
      alert(errorMessage); // Simple alert for now
    }
  }

  function setLoadingState(button, isLoading) {
    const spinner = button.querySelector('.loading-overlay__spinner');

    if (isLoading) {
      button.setAttribute('aria-disabled', 'true');
      button.classList.add('loading');
      if (spinner) spinner.classList.remove('hidden');
    } else {
      button.removeAttribute('aria-disabled');
      button.classList.remove('loading');
      if (spinner) spinner.classList.add('hidden');
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initializeWithEventDelegation();
    });
  } else {
    // DOM already loaded
    initializeWithEventDelegation();
  }
})(); // End of IIFE
